import {
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Optional,
    Output,
    QueryList,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import { CmpsDialogRef } from '@cmps/ng-uikit';

import { CmpsCommitmentSummaryContext } from './commitment-summary';
import { CmpsCommitmentSummaryGoalRowComponent } from './commitment-summary-goal-row.component';
import { CmpsCommitmentSummaryNoteRowComponent } from './commitment-summary-note-row.component';
import { CmpsCommitmentSummaryObservationRowComponent } from './commitment-summary-observation-row.component';

@Directive({
    selector: '[cmpsCoachingCommitmentSummaryGoalRows]',
})
export class CmpsCommitmentSummaryGoalRowDirective {
    constructor(public templateRef: TemplateRef<any>) {}
}

@Directive({
    selector: '[cmpsCoachingCommitmentSummaryObservationRows]',
})
export class CmpsCommitmentSummaryObservationRowDirective {
    constructor(public templateRef: TemplateRef<any>) {}
}

@Directive({
    selector: '[cmpsCoachingCommitmentSummaryNoteRows]',
})
export class CmpsCommitmentSummaryNoteRowDirective {
    constructor(public templateRef: TemplateRef<any>) {}
}

// revise in the case that multiple instances of the component need to coexist at the same time
const context = new CmpsCommitmentSummaryContext();

export function contextFactory(): CmpsCommitmentSummaryContext {
    return context;
}

@Component({
    selector: 'cmps-coaching-commitment-summary',
    template: ` <div class="cmps-coaching-commitment-summary--header">
            <div class="cmps-coaching-commitment-summary--header--detail">
                <div>{{ commitmentDetailLabel }}</div>
            </div>
            <div class="cmps-coaching-commitment-summary--header--buttons">
                <div class="cmps-coaching-commitment-summary--close">
                    <button
                        class="cmps-icon-button--no-bg"
                        cmpsIconButton
                        (click)="_dialogRef?.close()"
                    >
                        <cmps-icon svgIcon="__internal:close"></cmps-icon>
                    </button>
                </div>
                <button
                    class="cmps-outline-button--sm cmps-outline-button--no-bg cmps-coaching-commitment-summary--actions--secondary"
                    cmpsOutlineButton
                    (click)="acceptAllAction.emit($event)"
                    *ngIf="showDisputeView"
                >
                    <span cmpsOutlineButtonLabel>{{
                        acceptAllActionLabel
                    }}</span>
                </button>
            </div>
        </div>
        <div class="cmps-coaching-commitment-summary--content">
            <div
                class="cmps-coaching-commitment-summary--section"
                *ngIf="isShowNotes"
            >
                <div class="cmps-coaching-commitment-summary--section--header">
                    {{ commitmentNarrativeLabel }}
                </div>
                <div class="cmps-coaching-commitment-summary--section--content">
                    <div
                        class="cmps-coaching-commitment-summary--note--row-header"
                        [class.cmps-coaching-commitment-summary--note--with-actions]="
                            _context.showActionButtons ||
                            _context.showActionLabel
                        "
                    >
                        <div
                            class="cmps-coaching-commitment-summary--note--row-header--left"
                        >
                            <div>{{ modifiedElementLabel }}</div>
                            <div>{{ actionTakenLabel }}</div>
                            <div>{{ userLabel }}</div>
                            <div>{{ sessionLabel }}</div>
                        </div>
                        <div
                            class="cmps-coaching-commitment-summary--note--row-header--actions"
                        ></div>
                    </div>
                    <ng-container
                        *ngTemplateOutlet="_noteRowTpl?.templateRef"
                    ></ng-container>
                </div>
            </div>
            <div
                class="cmps-coaching-commitment-summary--section"
                *ngIf="isShowObservation"
            >
                <div class="cmps-coaching-commitment-summary--section--header">
                    {{ observationDetailsLabel }}
                </div>
                <div class="cmps-coaching-commitment-summary--section--content">
                    <div
                        class="cmps-coaching-commitment-summary--obs--row-header"
                        [class.cmps-coaching-commitment-summary--obs--with-actions]="
                            _context.showActionButtons ||
                            _context.showActionLabel
                        "
                    >
                        <div
                            class="cmps-coaching-commitment-summary--obs--row-header--left"
                        >
                            <div>{{ modifiedElementLabel }}</div>
                            <div>{{ actionTakenLabel }}</div>
                            <div>{{ dateLabel }}</div>
                            <div>{{ sessionTypeLabel }}</div>
                            <div>{{ userLabel }}</div>
                            <div>{{ sessionLabel }}</div>
                        </div>
                        <div
                            class="cmps-coaching-commitment-summary--obs--row--header--actions"
                        ></div>
                    </div>
                    <ng-container
                        *ngTemplateOutlet="_observationRowTpl?.templateRef"
                    ></ng-container>
                </div>
            </div>
            <div
                class="cmps-coaching-commitment-summary--section"
                *ngIf="isShowGoals"
            >
                <div class="cmps-coaching-commitment-summary--section--header">
                    {{ goalDetailsLabel }}
                </div>
                <div class="cmps-coaching-commitment-summary--section--content">
                    <div
                        class="cmps-coaching-commitment-summary--goal--row-header"
                        [class.cmps-coaching-commitment-summary--goal--with-actions]="
                            _context.showActionButtons ||
                            _context.showActionLabel
                        "
                    >
                        <div
                            class="cmps-coaching-commitment-summary--goal--row-header--left"
                        >
                            <div>{{ modifiedElementLabel }}</div>
                            <div>{{ actionTakenLabel }}</div>
                            <div>{{ previousGoalLabel }}</div>
                            <div>{{ newGoalLabel }}</div>
                            <div>{{ userLabel }}</div>
                            <div>{{ sessionLabel }}</div>
                        </div>
                        <div
                            class="cmps-coaching-commitment-summary--goal--row-header--actions"
                        ></div>
                    </div>
                    <ng-container
                        *ngTemplateOutlet="_goalRowTpl?.templateRef"
                    ></ng-container>
                </div>
            </div>
        </div>
        <ng-container *ngIf="showSenderView">
            <div class="cmps-coaching-commitment-summary--actions">
                <button
                    class="cmps-outline-button--sm cmps-outline-button--no-bg"
                    cmpsOutlineButton
                    (click)="_dialogRef?.close()"
                >
                    <span cmpsOutlineButtonLabel>{{
                        keepEditingActionLabel
                    }}</span>
                </button>
                <button
                    class="cmps-outline-button--sm cmps-outline-button--no-bg cmps-coaching-commitment-summary--actions--secondary"
                    cmpsOutlineButton
                    (click)="sendApprovalAction.emit($event)"
                >
                    <span cmpsOutlineButtonLabel>{{
                        sendApprovalActionLabel
                    }}</span>
                </button>
                <button
                    class="cmps-button--sm"
                    cmpsButton
                    (click)="signOffAction.emit($event)"
                >
                    <span cmpsButtonLabel>{{ signOffActionLabel }}</span>
                </button>
            </div>
        </ng-container>
        <ng-container *ngIf="showReceiverView">
            <div class="cmps-coaching-commitment-summary--actions">
                <button
                    class="cmps-outline-button--sm cmps-outline-button--no-bg cmps-coaching-commitment-summary--actions--dispute"
                    cmpsOutlineButton
                    (click)="receiverDisputeAction.emit($event)"
                >
                    <span cmpsOutlineButtonLabel>{{
                        receiverDisputeLabel
                    }}</span>
                </button>
                <button
                    class="cmps-outline-button--sm cmps-outline-button--no-bg cmps-coaching-commitment-summary--actions--secondary"
                    cmpsOutlineButton
                    (click)="receiverAcceptAllSendAction.emit($event)"
                >
                    <span cmpsOutlineButtonLabel>{{
                        receiverAcceptAllSendLabel
                    }}</span>
                </button>
                <button
                    class="cmps-button--sm"
                    cmpsButton
                    (click)="_dialogRef?.close()"
                >
                    <span cmpsButtonLabel>{{
                        receiverGotoCommitmentLabel
                    }}</span>
                </button>
            </div>
        </ng-container>
        <ng-container *ngIf="showDisputeView">
            <div class="cmps-coaching-commitment-summary--actions">
                <button
                    class="cmps-outline-button--sm cmps-outline-button--no-bg"
                    cmpsOutlineButton
                    (click)="_dialogRef?.close()"
                >
                    <span cmpsOutlineButtonLabel>{{
                        receiverDisputeCancelLabel
                    }}</span>
                </button>
                <button
                    class="cmps-button--sm"
                    cmpsButton
                    [disabled]="isReceiverDisputeSendDisabled"
                    (click)="receiverDisputeSendAction.emit($event)"
                >
                    <span cmpsButtonLabel>{{ receiverDisputeSendLabel }}</span>
                </button>
            </div>
        </ng-container>
        <ng-container *ngIf="isShowGotoCommitmentAction">
            <div class="cmps-coaching-commitment-summary--actions">
                <button
                    class="cmps-button--sm"
                    cmpsButton
                    (click)="_dialogRef?.close()"
                >
                    <span cmpsButtonLabel>{{
                        receiverGotoCommitmentLabel
                    }}</span>
                </button>
            </div>
        </ng-container>`,
    host: {
        class: 'cmps-coaching-commitment-summary',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        { provide: CmpsCommitmentSummaryContext, useFactory: contextFactory },
    ],
})
export class CmpsCommitmentSummaryComponent implements OnDestroy {
    /** @private */
    @ContentChild(CmpsCommitmentSummaryNoteRowDirective)
    readonly _noteRowTpl: CmpsCommitmentSummaryNoteRowDirective;

    @ContentChildren(CmpsCommitmentSummaryNoteRowComponent)
    private readonly _noteRows: QueryList<CmpsCommitmentSummaryNoteRowComponent>;

    /** @private */
    @ContentChild(CmpsCommitmentSummaryObservationRowDirective)
    readonly _observationRowTpl: CmpsCommitmentSummaryObservationRowDirective;

    @ContentChildren(CmpsCommitmentSummaryObservationRowComponent)
    private readonly _observationRows: QueryList<CmpsCommitmentSummaryObservationRowComponent>;

    /** @private */
    @ContentChild(CmpsCommitmentSummaryGoalRowDirective)
    readonly _goalRowTpl: CmpsCommitmentSummaryGoalRowDirective;

    @ContentChildren(CmpsCommitmentSummaryGoalRowComponent)
    private readonly _goalRows: QueryList<CmpsCommitmentSummaryGoalRowComponent>;

    @Input() commitmentDetailLabel: string;

    @Input() commitmentNarrativeLabel: string;
    @Input() modifiedElementLabel: string;
    @Input() actionTakenLabel: string;
    @Input() userLabel: string;
    @Input() sessionLabel: string;
    @Input() isShowNotes: boolean;
    @Input() isShowGoals: boolean;
    @Input() isShowObservation: boolean;

    @Input() acceptAllActionLabel: string;
    @Output() acceptAllAction: EventEmitter<MouseEvent | KeyboardEvent>;

    @Input() observationDetailsLabel: string;
    @Input() dateLabel: string;
    @Input() sessionTypeLabel: string;

    @Input() goalDetailsLabel: string;
    @Input() previousGoalLabel: string;
    @Input() newGoalLabel: string;

    @Input() keepEditingActionLabel: string;

    @Input() sendApprovalActionLabel: string;
    @Output() sendApprovalAction: EventEmitter<MouseEvent | KeyboardEvent>;

    @Input() signOffActionLabel: string;
    @Output() signOffAction: EventEmitter<MouseEvent | KeyboardEvent>;

    @Input() receiverDisputeLabel: string;
    @Output() receiverDisputeAction: EventEmitter<MouseEvent | KeyboardEvent>;

    @Input() receiverAcceptAllSendLabel: string;
    @Output() receiverAcceptAllSendAction: EventEmitter<
        MouseEvent | KeyboardEvent
    >;

    @Input() receiverGotoCommitmentLabel: string;

    @Input() receiverDisputeCancelLabel: string;

    @Input() receiverDisputeSendLabel: string;
    @Input() isReceiverDisputeSendDisabled: boolean;

    @Output() receiverDisputeSendAction: EventEmitter<
        MouseEvent | KeyboardEvent
    >;

    @Input() isShowGotoCommitmentAction: boolean;
    @Output() gotoCommitmentAction: EventEmitter<MouseEvent | KeyboardEvent>;

    @Input() showSenderView: boolean;
    @Input() showReceiverView: boolean;
    @Input() showDisputeView: boolean;

    /** @private */ _context: CmpsCommitmentSummaryContext;

    // eslint-disable-next-line accessor-pairs
    @Input() set showActionButtons(value: boolean) {
        context.showActionButtons = value;
        this._check();
    }

    // eslint-disable-next-line accessor-pairs
    @Input() set showActionLabel(value: boolean) {
        context.showActionLabel = value;
        this._check();
    }

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef<HTMLElement>,
        /** @private */ @Optional()
        public readonly _dialogRef: CmpsDialogRef<any, any>,
    ) {
        this.acceptAllAction = new EventEmitter();
        this.sendApprovalAction = new EventEmitter();
        this.signOffAction = new EventEmitter();
        this.receiverDisputeAction = new EventEmitter();
        this.receiverAcceptAllSendAction = new EventEmitter();
        this.receiverDisputeSendAction = new EventEmitter();
        this.gotoCommitmentAction = new EventEmitter();

        this._context = context;
    }

    ngOnDestroy(): void {
        this.acceptAllAction.complete();
        this.sendApprovalAction.complete();
        this.signOffAction.complete();
        this.receiverDisputeAction.complete();
        this.receiverAcceptAllSendAction.complete();
        this.receiverDisputeSendAction.complete();
        this.gotoCommitmentAction.complete();
    }

    private _check(): void {
        this._noteRows?.forEach((e) => e._check());
        this._observationRows?.forEach((e) => e._check());
        this._goalRows?.forEach((e) => e._check());
    }
}
